import React, { useEffect, useRef, useState } from "react";
import { CaretDown } from "@phosphor-icons/react";

/**
 * Collapsible panel — reusable accordion-style section with optional persistent
 * open/close state via localStorage (if `persistKey` is provided).
 *
 * Pass `anchorId` to make the section linkable via URL hash (e.g. /profile#streak).
 * When that hash is present on mount, the section force-opens and scrolls into view.
 *
 * Keeps the design language consistent: `rule` border, serif heading, eyebrow meta.
 */
export default function Collapsible({
  icon,
  title,
  subtitle,
  children,
  defaultOpen = true,
  persistKey,
  testId,
  anchorId,
}) {
  const sectionRef = useRef(null);
  const [open, setOpen] = useState(() => {
    // Hash wins: deep-link always forces open
    try {
      if (anchorId && typeof window !== "undefined" && window.location.hash === `#${anchorId}`) {
        return true;
      }
    } catch {}
    if (!persistKey) return defaultOpen;
    try {
      const stored = localStorage.getItem(`ekkol_collapse_${persistKey}`);
      if (stored === "1") return true;
      if (stored === "0") return false;
    } catch {}
    return defaultOpen;
  });

  // On mount and on hash changes, scroll the anchored section into view
  useEffect(() => {
    if (!anchorId) return;
    const onHash = () => {
      if (window.location.hash === `#${anchorId}`) {
        setOpen(true);
        setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      }
    };
    onHash();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [anchorId]);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (persistKey) {
        try { localStorage.setItem(`ekkol_collapse_${persistKey}`, next ? "1" : "0"); } catch {}
      }
      return next;
    });
  };

  return (
    <section
      ref={sectionRef}
      id={anchorId}
      className="border rule bg-white mb-6"
      data-testid={testId}
      style={anchorId ? { scrollMarginTop: 80 } : undefined}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-[color:var(--surface-alt)] transition-colors"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {icon && (
            <div className="w-9 h-9 rounded-full bg-[color:var(--oxford)]/10 flex items-center justify-center flex-shrink-0">
              {icon}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="font-serif text-xl sm:text-2xl leading-tight">{title}</h2>
            {subtitle && <p className="text-xs text-[color:var(--ink-muted)] mt-0.5 truncate">{subtitle}</p>}
          </div>
        </div>
        <CaretDown
          size={16}
          className={`shrink-0 text-[color:var(--ink-muted)] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t rule px-6 py-5 fade-in" data-testid={testId ? `${testId}-body` : undefined}>
          {children}
        </div>
      )}
    </section>
  );
}
